import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Save, Library, Clock, Coins, Users, Shield } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';

const Section = ({ title, icon: Icon, color, children }: any) => (
  <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden',marginBottom:12}}>
    <div style={{padding:'14px 16px',borderBottom:'1px solid #1e2d45',display:'flex',alignItems:'center',gap:8}}>
      <div style={{width:28,height:28,borderRadius:8,background:color+'22',display:'flex',alignItems:'center',justifyContent:'center'}}>
        <Icon size={15} style={{color}} />
      </div>
      <span style={{fontSize:13,fontWeight:500,color:'var(--text-secondary)'}}>{title}</span>
    </div>
    <div style={{padding:16}}>{children}</div>
  </div>
);

const Field = ({ label, hint, children }: any) => (
  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,alignItems:'center',padding:'8px 0',borderBottom:'1px solid #0f1826'}}>
    <div>
      <div style={{fontSize:13,color:'var(--text-secondary)'}}>{label}</div>
      {hint && <div style={{fontSize:11,color:'var(--text-faint)',marginTop:2}}>{hint}</div>}
    </div>
    <div>{children}</div>
  </div>
);

const Input = (props: any) => (
  <input {...props} style={{width:'100%',background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8,padding:'7px 10px',fontSize:13,color:'var(--text-secondary)',outline:'none',boxSizing:'border-box'}} />
);

export default function SettingsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings'),
  } as any);

  const settings = (data as any)?.data?.settings || {};
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (settings && Object.keys(settings).length > 0) setForm(settings);
  }, [data]);

  const set = (k: string) => (e: any) => setForm((f: any) => ({...f, [k]: e.target.value}));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings', form);
      toast.success('პარამეტრები შენახულია!');
      qc.invalidateQueries({ queryKey: ['settings'] });
    } catch { toast.error('შეცდომა'); }
    finally { setSaving(false); }
  };

  if (isLoading) return <div style={{padding:24,color:'var(--text-faint)'}}>იტვირთება...</div>;

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif',maxWidth:760}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:600,color:'var(--text-primary)',margin:0}}>პარამეტრები</h1>
          <p style={{fontSize:12,color:'var(--text-faint)',marginTop:4}}>ბიბლიოთეკის კონფიგურაცია</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer',opacity:saving?0.6:1}}>
          <Save size={14} /> {saving ? 'შენახვა...' : 'შენახვა'}
        </button>
      </div>

      <Section title="ბიბლიოთეკის ინფო" icon={Library} color="#60a5fa">
        <Field label="სახელი" hint="ბიბლიოთეკის სრული სახელი">
          <Input value={form.library_name||''} onChange={set('library_name')} placeholder="Folios" />
        </Field>
        <Field label="ელ-ფოსტა" hint="საკონტაქტო მეილი">
          <Input type="email" value={form.library_email||''} onChange={set('library_email')} placeholder="library@example.ge" />
        </Field>
        <Field label="ინტერფეისის ენა" hint="სისტემის ენა">
          <select value={form.language||'ka'} onChange={set('language')}
            style={{width:'100%',background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8,padding:'7px 10px',fontSize:13,color:'var(--text-secondary)',outline:'none'}}>
            <option value="ka">ქართული</option>
            <option value="en">English</option>
          </select>
        </Field>
      </Section>

      <Section title="გაცემის წესები" icon={Clock} color="#fbbf24">
        <Field label="გაცემის ვადა" hint="სტანდარტული დღეების რაოდენობა">
          <Input type="number" value={form.loan_period_days||14} onChange={set('loan_period_days')} min={1} max={365} />
        </Field>
        <Field label="მაქს. გაცემა" hint="ერთ წევრზე მაქსიმუმი">
          <Input type="number" value={form.max_checkouts||5} onChange={set('max_checkouts')} min={1} max={50} />
        </Field>
      </Section>

      <Section title="ჯარიმები" icon={Coins} color="#f87171">
        <Field label="ჯარიმა/დღე" hint="გვიანდელი დაბრუნების ჯარიმა (₾)">
          <Input type="number" value={form.fine_per_day||0.50} onChange={set('fine_per_day')} min={0} step={0.10} />
        </Field>
      </Section>

      <Section title="სისტემის ინფო" icon={Shield} color="#34d399">
        <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:8}}>
          {[
            {label:'ვერსია', val:'1.0.0'},
            {label:'Node.js', val:'v22.x'},
            {label:'DB', val:'MySQL 8.0'},
            {label:'სერვერი', val:'Ubuntu 24.04'},
          ].map(r => (
            <div key={r.label} style={{padding:'10px 12px',background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8}}>
              <div style={{fontSize:11,color:'var(--text-faint)',marginBottom:3}}>{r.label}</div>
              <div style={{fontSize:13,color:'var(--success)',fontFamily:'monospace'}}>{r.val}</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
